import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { useSeccionPermisos } from '../hooks/useSeccionPermisos';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Play, Activity, Plus, Pencil, Trash2, X, FlaskConical, 
  AlertTriangle, CheckCircle, Loader2, Info, FolderOpen, 
  Download, Settings, Shield, MapPin, History, Briefcase, Copy, Save, Calendar,
  Users, User, Eye, Search
} from 'lucide-react';
import { getSeverityColor } from '../utils/styleUtils';
import { MonitorsDialogs } from '../components/monitors/MonitorsDialogs';

const SEVERITIES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'];
const OPERATORS = ['>', '<', '>=', '<=', '==', '!=', 'IN', 'BETWEEN'];
const MONITOR_TYPES = [
  { value: 'TRANSACCION_UNICA', label: 'Transacción Única', desc: 'Detecta transacciones individuales' },
  { value: 'ACUMULACION_MONTO', label: 'Acumulación de Monto', desc: 'Suma de montos en período' },
  { value: 'ACUMULACION_CANTIDAD', label: 'Acumulación de Cantidad', desc: 'Número de operaciones en período' },
  { value: 'FRACCIONAMIENTO', label: 'Fraccionamiento', desc: 'Montos similares repetidos' }
];
const VENTANAS = [
  { value: 'DIARIO', label: 'Diario', days: 1 },
  { value: 'SEMANAL', label: 'Semanal', days: 7 },
  { value: 'MENSUAL', label: 'Mensual', days: 30 }
];

const RISK_ZONE_TYPES = [
  { value: 'cbu_prefix', label: 'Prefijo de CBU' },
  { value: 'banco', label: 'Nombre de Banco' },
  { value: 'cuit_prefix', label: 'Prefijo de CUIT' },
  { value: 'exact', label: 'Valor Exacto' }
];

const emptyMonitor = {
  nombre: '',
  descripcion: '',
  severidad: 'MEDIA',
  activo: true,
  tipo_monitor: 'TRANSACCION_UNICA',
  ventana_temporal: null,
  umbral_acumulacion: null,
  campo_agrupacion: 'cvu_mov',
  criterios: [{ campo: '', operador: '>', valor: '', agrupacion: '' }]
};

export const Monitors = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('monitors');
  const [monitors, setMonitors] = useState([]);
  const permMonitores = useSeccionPermisos('monitoreo-transaccional', 'monitors');
  const puedeEjecutar = !!permMonitores?.ejecutar;
  const sinPermisoTxt = 'No tenés permiso para ejecutar monitores';
  const [groups, setGroups] = useState([]);
  const [riskZones, setRiskZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBacktestDialogOpen, setIsBacktestDialogOpen] = useState(false);
  const [isBacktestConfigOpen, setIsBacktestConfigOpen] = useState(false);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [isGroupExecutionOpen, setIsGroupExecutionOpen] = useState(false);
  const [isInstallDialogOpen, setIsInstallDialogOpen] = useState(false);
  const [isRiskZoneDialogOpen, setIsRiskZoneDialogOpen] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState(emptyMonitor);
  const [groupFormData, setGroupFormData] = useState({ nombre: '', descripcion: '', monitor_ids: [] });
  const [riskZoneFormData, setRiskZoneFormData] = useState({ nombre: '', tipo: 'cbu_prefix', valor: '', nivel_riesgo: 'ALTO', descripcion: '' });
  const [saving, setSaving] = useState(false);
  const [installing, setInstalling] = useState(false);
  
  // Backtesting state
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestResults, setBacktestResults] = useState(null);
  const [creatingAlert, setCreatingAlert] = useState({});
  const [backtestConfig, setBacktestConfig] = useState({
    cuit: '',
    fecha_desde: '',
    fecha_hasta: ''
  });
  
  // Selección múltiple para crear caso desde backtesting
  const [selectedBacktestResults, setSelectedBacktestResults] = useState([]);
  const [creatingCaseFromBacktest, setCreatingCaseFromBacktest] = useState(false);
  
  // Filtros de resultados de backtesting
  const [backtestFilters, setBacktestFilters] = useState({
    montoMinimo: '',
    montoMaximo: '',
    minTransacciones: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [showBacktestFilters, setShowBacktestFilters] = useState(false);
  
  // Group execution state
  const [groupExecutionLoading, setGroupExecutionLoading] = useState(false);
  const [groupExecutionResults, setGroupExecutionResults] = useState(null);
  
  // Execute all monitors state
  const [isExecuteAllDialogOpen, setIsExecuteAllDialogOpen] = useState(false);
  const [executeAllLoading, setExecuteAllLoading] = useState(false);
  const [executeAllResults, setExecuteAllResults] = useState(null);
  const [generateAlertsAfterBacktest, setGenerateAlertsAfterBacktest] = useState(false);
  const [alertGenerationMode, setAlertGenerationMode] = useState('grouped'); // 'grouped' (por defecto) o 'individual'
  const [selectedMonitorsForAlerts, setSelectedMonitorsForAlerts] = useState([]); // Para selección manual de qué monitores generar
  const [forzarNuevosCasos, setForzarNuevosCasos] = useState(false); // Forzar creación sin verificar duplicados
  
  // Nuevo: Modos de ejecución
  const [modoEjecucion, setModoEjecucion] = useState('TODOS'); // TODOS, CLIENTE, VISUALIZACION
  const [clienteFilter, setClienteFilter] = useState('');
  const [clienteSearch, setClienteSearch] = useState('');
  const [clientesEncontrados, setClientesEncontrados] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  
  // Config global de fecha base para monitores
  const [globalConfig, setGlobalConfig] = useState({ fechaBaseGlobal: null });
  const [showGlobalConfigDialog, setShowGlobalConfigDialog] = useState(false);

  // Toggle selección de resultado de backtesting
  const toggleBacktestSelection = (match, idx) => {
    const key = `${match.cuenta_id}-${idx}`;
    setSelectedBacktestResults(prev => 
      prev.find(s => s.key === key) 
        ? prev.filter(s => s.key !== key)
        : [...prev, { key, match, idx }]
    );
  };
  
  // Filtrar resultados de backtesting
  const getFilteredBacktestResults = () => {
    if (!backtestResults?.coincidencias) return [];
    
    return backtestResults.coincidencias.filter(match => {
      // Filtro por monto mínimo
      if (backtestFilters.montoMinimo && match.monto_total !== undefined) {
        if (match.monto_total < parseFloat(backtestFilters.montoMinimo)) return false;
      }
      
      // Filtro por monto máximo
      if (backtestFilters.montoMaximo && match.monto_total !== undefined) {
        if (match.monto_total > parseFloat(backtestFilters.montoMaximo)) return false;
      }
      
      // Filtro por cantidad mínima de transacciones
      if (backtestFilters.minTransacciones && match.cantidad_transacciones !== undefined) {
        if (match.cantidad_transacciones < parseInt(backtestFilters.minTransacciones)) return false;
      }
      
      // Filtro por fecha (si el match tiene fecha)
      if (backtestFilters.fechaDesde && match.fecha) {
        const matchDate = new Date(match.fecha);
        const filterDate = new Date(backtestFilters.fechaDesde);
        if (matchDate < filterDate) return false;
      }
      
      if (backtestFilters.fechaHasta && match.fecha) {
        const matchDate = new Date(match.fecha);
        const filterDate = new Date(backtestFilters.fechaHasta);
        if (matchDate > filterDate) return false;
      }
      
      return true;
    });
  };
  
  // Limpiar filtros de backtesting
  const clearBacktestFilters = () => {
    setBacktestFilters({
      montoMinimo: '',
      montoMaximo: '',
      minTransacciones: '',
      fechaDesde: '',
      fechaHasta: ''
    });
  };
  
  // Crear caso con múltiples alertas desde backtesting
  const handleCreateCaseFromBacktest = async () => {
    if (selectedBacktestResults.length === 0) {
      toast.error('Seleccione al menos un resultado');
      return;
    }
    
    setCreatingCaseFromBacktest(true);
    try {
      // Primero crear las alertas para cada resultado seleccionado
      const alertasIds = [];
      const cuentasRelacionadas = new Set();
      
      for (const { match } of selectedBacktestResults) {
        // Crear alerta si no existe
        if (!match.alertaCreada) {
          const alertData = {
            titulo: `${selectedMonitor?.nombre || 'Monitor'}: ${match.titulo_detalle}`,
            descripcion: match.descripcion,
            severidad: selectedMonitor?.severidad || 'ALTA',
            tipo: 'MONITOR',
            cuenta_id: match.cuenta_id,
            monitor_id: selectedMonitor?.id,
            origen: 'BACKTESTING',
            movimientos_ids: match.movimientos_ids || []
          };
          
          const alertRes = await api.post('/alerts', alertData);
          alertasIds.push(alertRes.data.id);
          match.alertaCreada = true;
        }
        
        if (match.cuenta_id) {
          cuentasRelacionadas.add(match.cuenta_id);
        }
      }
      
      // Luego crear el caso con todas las alertas
      const caseData = {
        titulo: `Caso: ${selectedMonitor?.nombre || 'Monitor'} - ${selectedBacktestResults.length} hallazgos`,
        descripcion: `Caso generado desde backtesting del monitor "${selectedMonitor?.nombre}". Incluye ${selectedBacktestResults.length} hallazgos sospechosos.`,
        tipo: 'ALERTA',
        prioridad: selectedMonitor?.severidad === 'CRITICA' ? 'CRITICA' : 'ALTA',
        alertas_ids: alertasIds,
        cuentas_relacionadas: Array.from(cuentasRelacionadas),
        tags: ['backtesting', selectedMonitor?.nombre?.toLowerCase().replace(/\s/g, '-')]
      };
      
      const caseRes = await api.post('/cases', caseData);
      toast.success(`Caso creado con ${alertasIds.length} alertas`);
      setSelectedBacktestResults([]);
      setIsBacktestDialogOpen(false);
      
      // Navegar al caso
      navigate(`/m/gestion-de-casos/caso/${caseRes.data.id}`);
    } catch (error) {
      console.error('Error creating case:', error);
      toast.error('Error al crear caso');
    } finally {
      setCreatingCaseFromBacktest(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [monitorsRes, groupsRes, zonesRes, configRes] = await Promise.all([
        api.get('/monitors'),
        api.get('/monitor-groups'),
        api.get('/predefined/risk-zones'),
        api.get('/monitors/config/global').catch(() => ({ data: { fecha_base_global: null } }))
      ]);
      setMonitors(monitorsRes.data);
      setGroups(groupsRes.data);
      setRiskZones(zonesRes.data);
      setGlobalConfig(configRes.data || { fecha_base_global: null });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalConfig = async () => {
    try {
      await api.put('/monitors/config/global', {
        fecha_base_global: globalConfig.fechaBaseGlobal,
        descripcion: globalConfig.descripcion
      });
      toast.success('Configuración guardada');
      setShowGlobalConfigDialog(false);
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const loadMonitors = async () => {
    try {
      const response = await api.get('/monitors');
      setMonitors(response.data);
    } catch (error) {
      toast.error('Error al cargar monitores');
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/monitor-groups');
      setGroups(response.data);
    } catch (error) {
      toast.error('Error al cargar grupos');
    }
  };

  const loadRiskZones = async () => {
    try {
      const response = await api.get('/predefined/risk-zones');
      setRiskZones(response.data);
    } catch (error) {
      toast.error('Error al cargar zonas de riesgo');
    }
  };

  // ========== MONITOR HANDLERS ==========
  const handleToggleActive = async (monitorId, currentStatus) => {
    try {
      await api.patch(`/monitors/${monitorId}`, { activo: !currentStatus });
      toast.success(`Monitor ${!currentStatus ? 'activado' : 'desactivado'}`);
      loadMonitors();
    } catch (error) {
      toast.error('Error al actualizar monitor');
    }
  };

  const handleExecuteMonitor = async (monitorId, nombre) => {
    try {
      toast.info(`Ejecutando monitor: ${nombre}`);
      await api.post(`/monitors/${monitorId}/execute`, null, {
        params: { mode: 'PRODUCTIVO' },
      });
      toast.success('Monitor ejecutado correctamente');
      loadMonitors();
    } catch (error) {
      toast.error('Error al ejecutar monitor');
    }
  };

  const handleBacktestMonitor = async (monitor) => {
    setSelectedMonitor(monitor);
    setBacktestConfig({ cuit: '', fecha_desde: '', fecha_hasta: '' });
    setIsBacktestConfigOpen(true);
  };

  const executeBacktest = async () => {
    setIsBacktestConfigOpen(false);
    setBacktestResults(null);
    setIsBacktestDialogOpen(true);
    setBacktestLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (backtestConfig.cuit) params.append('cuit', backtestConfig.cuit);
      if (backtestConfig.fecha_desde) params.append('fecha_desde', new Date(backtestConfig.fecha_desde).toISOString());
      if (backtestConfig.fecha_hasta) params.append('fecha_hasta', new Date(backtestConfig.fecha_hasta).toISOString());
      
      const queryString = params.toString();
      const url = `/monitors/${selectedMonitor.id}/backtest${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.post(url);
      setBacktestResults(response.data);
      
      if (response.data.total_coincidencias === 0) {
        toast.info('No se encontraron coincidencias');
      } else {
        toast.success(`Se encontraron ${response.data.total_coincidencias} coincidencias`);
      }
    } catch (error) {
      toast.error('Error al ejecutar backtesting');
      setBacktestResults({ error: true, message: error.response?.data?.detail || 'Error desconocido' });
    } finally {
      setBacktestLoading(false);
    }
  };

  const handleCreateHistoricalAlert = async (match) => {
    if (!selectedMonitor) return;
    
    const matchKey = match.cuenta_id;
    setCreatingAlert(prev => ({ ...prev, [matchKey]: true }));
    
    try {
      await api.post('/monitors/historical-alert', {
        monitor_id: selectedMonitor.id,
        cuenta_id: match.cuenta_id,
        titulo: `${selectedMonitor.nombre}: ${match.titulo_detalle}`,
        descripcion: match.descripcion,
        movimientos_ids: match.movimientos_ids || []
      });
      
      toast.success(`Alerta creada para cuenta ${match.cuenta_id}`);
      setBacktestResults(prev => ({
        ...prev,
        coincidencias: prev.coincidencias.map(c => 
          c.cuenta_id === match.cuenta_id ? { ...c, alertaCreada: true } : c
        )
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear alerta');
    } finally {
      setCreatingAlert(prev => ({ ...prev, [matchKey]: false }));
    }
  };

  const openCreateDialog = () => {
    setFormData(emptyMonitor);
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (monitor) => {
    setSelectedMonitor(monitor);
    setFormData({
      nombre: monitor.nombre,
      descripcion: monitor.descripcion,
      severidad: monitor.severidad,
      activo: monitor.activo,
      tipo_monitor: monitor.tipo_monitor || 'TRANSACCION_UNICA',
      ventana_temporal: monitor.ventana_temporal,
      umbral_acumulacion: monitor.umbral_acumulacion,
      campo_agrupacion: monitor.campo_agrupacion || 'cuit',
      criterios: monitor.criterios.length > 0 
        ? monitor.criterios 
        : [{ campo: '', operador: '>', valor: '', agrupacion: '' }]
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (monitor) => {
    setSelectedMonitor(monitor);
    setIsDeleteDialogOpen(true);
  };

  const handleCreateMonitor = async () => {
    if (!formData.nombre || !formData.descripcion) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }

    const validCriterios = formData.criterios.filter(c => c.campo && c.operador && c.valor);
    
    setSaving(true);
    try {
      await api.post('/monitors', {
        ...formData,
        criterios: validCriterios
      });
      toast.success('Monitor creado correctamente');
      setIsCreateDialogOpen(false);
      loadMonitors();
    } catch (error) {
      toast.error('Error al crear monitor');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMonitor = async () => {
    if (!formData.nombre || !formData.descripcion) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }

    const validCriterios = formData.criterios.filter(c => c.campo && c.operador && c.valor);

    setSaving(true);
    try {
      await api.patch(`/monitors/${selectedMonitor.id}`, {
        ...formData,
        criterios: validCriterios
      });
      toast.success('Monitor actualizado correctamente');
      setIsEditDialogOpen(false);
      loadMonitors();
    } catch (error) {
      toast.error('Error al actualizar monitor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMonitor = async () => {
    setSaving(true);
    try {
      await api.delete(`/monitors/${selectedMonitor.id}`);
      toast.success('Monitor eliminado correctamente');
      setIsDeleteDialogOpen(false);
      loadMonitors();
    } catch (error) {
      toast.error('Error al eliminar monitor');
    } finally {
      setSaving(false);
    }
  };

  // Duplicar un monitor existente
  const handleDuplicateMonitor = async (monitor) => {
    try {
      const duplicatedData = {
        nombre: `${monitor.nombre} (Copia)`,
        descripcion: monitor.descripcion || '',
        severidad: monitor.severidad,
        tipo_monitor: monitor.tipo_monitor,
        ventana_temporal: monitor.ventana_temporal,
        umbral_acumulacion: monitor.umbral_acumulacion,
        campo_agrupacion: monitor.campo_agrupacion,
        criterios: monitor.criterios || [],
        grupo_id: monitor.grupo_id,
        logica_especial: monitor.logica_especial
      };
      
      const response = await api.post('/monitors/', duplicatedData);
      toast.success(`Monitor duplicado: "${response.data.nombre}"`);
      loadMonitors();
    } catch (error) {
      toast.error('Error al duplicar monitor');
    }
  };
  
  // Actualización rápida del umbral de un monitor
  const handleQuickUpdateUmbral = async (monitorId, nuevoUmbral) => {
    try {
      await api.patch(`/monitors/${monitorId}`, {
        umbral_acumulacion: nuevoUmbral
      });
      toast.success(`Umbral actualizado a ${nuevoUmbral}`);
      loadMonitors();
    } catch (error) {
      toast.error('Error al actualizar umbral');
    }
  };

  const handleUpdateParametros = async (monitorId, parametros) => {
    try {
      await api.patch(`/monitors/${monitorId}`, { parametros });
      toast.success('Parámetros actualizados');
      loadMonitors();
    } catch (error) {
      toast.error('Error al actualizar parámetros');
    }
  };

  const addCriterio = () => {
    setFormData({
      ...formData,
      criterios: [...formData.criterios, { campo: '', operador: '>', valor: '', agrupacion: '' }]
    });
  };

  const removeCriterio = (index) => {
    if (formData.criterios.length > 1) {
      setFormData({
        ...formData,
        criterios: formData.criterios.filter((_, i) => i !== index)
      });
    }
  };

  const updateCriterio = (index, field, value) => {
    const newCriterios = [...formData.criterios];
    newCriterios[index] = { ...newCriterios[index], [field]: value };
    setFormData({ ...formData, criterios: newCriterios });
  };

  // ========== GROUP HANDLERS ==========
  const openGroupDialog = (group = null) => {
    if (group) {
      setSelectedGroup(group);
      setGroupFormData({
        nombre: group.nombre,
        descripcion: group.descripcion || '',
        monitor_ids: group.monitor_ids || []
      });
    } else {
      setSelectedGroup(null);
      setGroupFormData({ nombre: '', descripcion: '', monitor_ids: [] });
    }
    setIsGroupDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!groupFormData.nombre) {
      toast.error('El nombre del grupo es requerido');
      return;
    }

    setSaving(true);
    try {
      if (selectedGroup) {
        await api.patch(`/monitor-groups/${selectedGroup.id}`, groupFormData);
        toast.success('Grupo actualizado correctamente');
      } else {
        await api.post('/monitor-groups/', groupFormData);
        toast.success('Grupo creado correctamente');
      }
      setIsGroupDialogOpen(false);
      loadGroups();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al guardar grupo';
      toast.error(errorMsg);
      console.error('Error al guardar grupo:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('¿Está seguro de eliminar este grupo?')) return;
    
    try {
      await api.delete(`/monitor-groups/${groupId}`);
      toast.success('Grupo eliminado');
      loadGroups();
    } catch (error) {
      toast.error('Error al eliminar grupo');
    }
  };

  const handleExecuteGroup = async (group) => {
    setSelectedGroup(group);
    setGroupExecutionResults(null);
    setIsGroupExecutionOpen(true);
    setGroupExecutionLoading(true);
    
    try {
      const response = await api.post(`/monitor-groups/${group.id}/execute`, null, {
        params: { mode: 'PRODUCTIVO', generar_casos: true }
      });
      const execId = response.data?.execution_id;
      // Compatibilidad: si el backend respondiera el resultado directo (legacy)
      if (!execId) {
        setGroupExecutionResults(response.data);
        setGroupExecutionLoading(false);
        return;
      }
      toast.info('Ejecución iniciada. Procesando en segundo plano...');
      // Polling del resultado (la ejecución corre en background → evita timeout 504)
      let elapsed = 0;
      const poll = setInterval(async () => {
        elapsed += 4000;
        try {
          const r = await api.get(`/monitor-groups/executions/${execId}`, { skipAuthRedirect: true });
          const data = r.data;
          if (data?.estado && data.estado !== 'PROCESANDO') {
            clearInterval(poll);
            setGroupExecutionLoading(false);
            if (data.estado === 'ERROR') {
              setGroupExecutionResults({ error: true, message: (data.errores && data.errores[0]) || 'Error al ejecutar el grupo' });
              toast.error('La ejecución del grupo falló');
            } else {
              setGroupExecutionResults(data);
              toast.success(`Grupo ejecutado: ${data.total_alertas_generadas} casos creados/actualizados`);
            }
            loadGroups();
          }
        } catch { /* seguir polleando */ }
        if (elapsed > 600000) {
          clearInterval(poll);
          setGroupExecutionLoading(false);
          setGroupExecutionResults({ error: true, message: 'La ejecución está tardando demasiado. Revisá el historial de ejecuciones más tarde.' });
        }
      }, 4000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al ejecutar grupo';
      toast.error(errorMsg);
      setGroupExecutionResults({ error: true, message: errorMsg });
      setGroupExecutionLoading(false);
    }
  };

  // ========== BUSCAR CLIENTE EN PADRON ==========
  const handleBuscarCliente = async (query) => {
    if (!query || query.length < 2) {
      setClientesEncontrados([]);
      return;
    }
    
    setBuscandoCliente(true);
    try {
      const response = await api.get('/monitors/buscar-cliente', {
        params: { q: query, limit: 10 }
      });
      setClientesEncontrados(response.data.resultados || []);
    } catch (error) {
      console.error('Error buscando cliente:', error);
      setClientesEncontrados([]);
    } finally {
      setBuscandoCliente(false);
    }
  };

  // Debounce para búsqueda de cliente
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clienteSearch && clienteSearch.length >= 2) {
        handleBuscarCliente(clienteSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clienteSearch]);

  // ========== EXECUTE ALL MONITORS ==========
  const handleExecuteAllMonitors = async () => {
    setExecuteAllResults(null);
    setIsExecuteAllDialogOpen(true);
    setExecuteAllLoading(true);
    setGenerateAlertsAfterBacktest(false);
    // Reset modo a TODOS cuando se abre el diálogo
    setModoEjecucion('TODOS');
    setClienteFilter('');
    setClienteSeleccionado(null);
    setClienteSearch('');
    setClientesEncontrados([]);
    
    const activeMonitors = monitors.filter(m => m.activo);
    const results = {
      total_monitores: activeMonitors.length,
      ejecutados: 0,
      con_coincidencias: 0,
      total_coincidencias: 0,
      detalle_por_monitor: []
    };
    
    try {
      for (const monitor of activeMonitors) {
        try {
          const response = await api.post(`/monitors/${monitor.id}/backtest`, null, {
            params: { mode: 'BACKTESTING' }
          });
          
          results.ejecutados++;
          const coincidencias = response.data.total_coincidencias || 0;
          results.total_coincidencias += coincidencias;
          
          if (coincidencias > 0) {
            results.con_coincidencias++;
          }
          
          results.detalle_por_monitor.push({
            id: monitor.id,
            nombre: monitor.nombre,
            severidad: monitor.severidad,
            coincidencias: coincidencias,
            preview: response.data.coincidencias?.slice(0, 3) || []
          });
        } catch (error) {
          results.detalle_por_monitor.push({
            id: monitor.id,
            nombre: monitor.nombre,
            severidad: monitor.severidad,
            coincidencias: 0,
            error: error.message
          });
        }
      }
      
      // Sort by coincidences descending
      results.detalle_por_monitor.sort((a, b) => b.coincidencias - a.coincidencias);
      setExecuteAllResults(results);
      toast.success(`Análisis completado: ${results.total_coincidencias} coincidencias en ${results.con_coincidencias} monitores`);
    } catch (error) {
      toast.error('Error al ejecutar monitores');
    } finally {
      setExecuteAllLoading(false);
    }
  };

  const handleGenerateAlertsFromResults = async () => {
    if (!executeAllResults) return;
    
    setGenerateAlertsAfterBacktest(true);
    let alertasGeneradas = 0;
    
    try {
      // Filtrar monitores según selección (si hay selección manual) o todos con coincidencias
      let monitoresAGenerar = executeAllResults.detalle_por_monitor.filter(m => m.coincidencias > 0);
      
      if (selectedMonitorsForAlerts.length > 0) {
        monitoresAGenerar = monitoresAGenerar.filter(m => selectedMonitorsForAlerts.includes(m.id));
      }
      
      if (alertGenerationMode === 'grouped') {
        // Modo agrupado: crear un caso por cada tipo de monitor/alerta
        try {
          // Enviar los datos de coincidencias al backend
          const coincidenciasData = monitoresAGenerar.map(m => ({
            monitor_id: m.id,
            monitor_nombre: m.nombre,
            coincidencias: m.coincidencias,
            detalle: m.preview || m.detalle || []  // Lista de detecciones si está disponible
          }));
          
          
          const response = await api.post('/monitors/generate-grouped-alert', {
            monitor_ids: monitoresAGenerar.map(m => m.id),
            titulo: `Casos desde monitores`,
            descripcion: `Casos generados automáticamente`,
            coincidencias_data: coincidenciasData,
            forzar_nuevos: forzarNuevosCasos,
            modo: modoEjecucion,
            cliente_filter: clienteFilter || clienteSeleccionado?.cuit || clienteSeleccionado?.cvu || null
          });
          
          
          // Manejar respuesta según el modo
          if (modoEjecucion === 'VISUALIZACION') {
            // Modo visualización - mostrar resultados sin crear casos
            toast.info(`Visualización: ${response.data.total_detecciones} detecciones en ${response.data.total_clientes} clientes`, {
              description: 'Modo visualización - no se crearon casos',
              duration: 5000
            });
            // Actualizar resultados para mostrar preview
            setExecuteAllResults(prev => ({
              ...prev,
              modo: 'VISUALIZACION',
              preview: response.data.preview || [],
              total_clientes: response.data.total_clientes || 0
            }));
            return;
          }
          
          const casosCreados = response.data.casos_creados || 0;
          const casosActualizados = response.data.casos_actualizados || 0;
          const totalDetecciones = response.data.total_detecciones || 0;
          const casosOmitidos = response.data.casos_omitidos || 0;
          
          if (casosCreados > 0 || casosActualizados > 0) {
            let mensaje = [];
            if (casosCreados > 0) mensaje.push(`${casosCreados} casos nuevos`);
            if (casosActualizados > 0) mensaje.push(`${casosActualizados} casos actualizados`);
            if (casosOmitidos > 0) mensaje.push(`${casosOmitidos} omitidos`);
            
            toast.success(`${mensaje.join(', ')} con ${totalDetecciones} detecciones totales`);
            alertasGeneradas = casosCreados + casosActualizados;
          } else if (casosOmitidos > 0) {
            toast.info(`No se crearon casos nuevos. ${casosOmitidos} caso(s) ya tenían las mismas alertas.`, {
              description: 'Las alertas detectadas ya existían en los casos.',
              duration: 6000
            });
          } else {
            toast.info('No se encontraron detecciones para generar casos');
          }
        } catch (error) {
          console.error('Error creando casos:', error);
          toast.error('Error al crear casos');
        }
      } else {
        // Modo individual: generar alertas separadas para cada monitor
        for (const monitor of monitoresAGenerar) {
          try {
            const response = await api.post(`/monitors/${monitor.id}/execute`, null, {
              params: { mode: 'PRODUCTIVO' }
            });
            alertasGeneradas += response.data.alertas_generadas || 0;
          } catch (error) {
            console.error(`Error generando alertas para ${monitor.nombre}:`, error);
          }
        }
        toast.success(`${alertasGeneradas} alertas generadas correctamente`);
      }
      
      setIsExecuteAllDialogOpen(false);
      setSelectedMonitorsForAlerts([]);
      loadData();
    } catch (error) {
      toast.error('Error al generar alertas');
    } finally {
      setGenerateAlertsAfterBacktest(false);
    }
  };

  const toggleMonitorInGroup = (monitorId) => {
    const current = groupFormData.monitor_ids || [];
    if (current.includes(monitorId)) {
      setGroupFormData({
        ...groupFormData,
        monitor_ids: current.filter(id => id !== monitorId)
      });
    } else {
      setGroupFormData({
        ...groupFormData,
        monitor_ids: [...current, monitorId]
      });
    }
  };

  // ========== INSTALL PREDEFINED ==========
  const handleInstallPredefined = async () => {
    setInstalling(true);
    try {
      // Instalar monitores
      const monitorsRes = await api.post('/predefined/monitors/install', {});
      toast.success(`${monitorsRes.data.installed_count} monitores instalados`);
      
      // Instalar grupos
      const groupsRes = await api.post('/predefined/groups/install');
      toast.success(`${groupsRes.data.created_count} grupos creados`);
      
      setIsInstallDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al instalar configuración predefinida');
    } finally {
      setInstalling(false);
    }
  };

  // ========== RISK ZONE HANDLERS ==========
  const openRiskZoneDialog = (zone = null) => {
    if (zone) {
      setRiskZoneFormData({
        id: zone.id,
        nombre: zone.nombre,
        tipo: zone.tipo,
        valor: zone.valor,
        nivel_riesgo: zone.nivel_riesgo || 'ALTO',
        descripcion: zone.descripcion || ''
      });
    } else {
      setRiskZoneFormData({ nombre: '', tipo: 'cbu_prefix', valor: '', nivel_riesgo: 'ALTO', descripcion: '' });
    }
    setIsRiskZoneDialogOpen(true);
  };

  const handleSaveRiskZone = async () => {
    if (!riskZoneFormData.nombre || !riskZoneFormData.valor) {
      toast.error('Nombre y valor son requeridos');
      return;
    }

    setSaving(true);
    try {
      if (riskZoneFormData.id) {
        await api.patch(`/predefined/risk-zones/${riskZoneFormData.id}`, riskZoneFormData);
        toast.success('Zona actualizada');
      } else {
        await api.post('/predefined/risk-zones', riskZoneFormData);
        toast.success('Zona de riesgo creada');
      }
      setIsRiskZoneDialogOpen(false);
      loadRiskZones();
    } catch (error) {
      toast.error('Error al guardar zona');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRiskZone = async (zoneId) => {
    if (!confirm('¿Eliminar esta zona de riesgo?')) return;
    
    try {
      await api.delete(`/predefined/risk-zones/${zoneId}`);
      toast.success('Zona eliminada');
      loadRiskZones();
    } catch (error) {
      toast.error('Error al eliminar zona');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Gestión de Monitores
          </h1>
          <p className="text-slate-600 mt-1">
            {monitors.filter((m) => m.activo).length} de {monitors.length} monitores activos · {groups.length} grupos
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button
            onClick={handleExecuteAllMonitors}
            disabled={!puedeEjecutar}
            title={puedeEjecutar ? undefined : sinPermisoTxt}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="execute-all-monitors-btn"
          >
            <Play size={18} className="mr-2" />
            Ejecutar Todos
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowGlobalConfigDialog(true)}
            className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
            data-testid="global-config-btn"
          >
            <Settings size={18} className="mr-2" />
            Configuración
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsInstallDialogOpen(true)}
            className="border-purple-500 bg-white hover:bg-purple-50 font-medium"
            style={{ color: '#6b21a8' }}
          >
            <Download size={18} className="mr-2" style={{ color: '#7c3aed' }} />
            <span style={{ color: '#6b21a8' }}>Instalar Predefinidos</span>
          </Button>
        </div>
      </div>
      
      {/* Indicador de fecha base configurada */}
      {globalConfig.fechaBaseGlobal || globalConfig.fecha_base_global ? (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-blue-600" />
            <span className="text-sm text-blue-800">
              <strong>Fecha base configurada:</strong> {new Date(globalConfig.fechaBaseGlobal || globalConfig.fecha_base_global).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
            <span className="text-xs text-blue-600 ml-2">
              (Los monitores solo analizarán datos desde esta fecha)
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGlobalConfigDialog(true)}
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
          >
            <Settings size={14} className="mr-1" />
            Cambiar
          </Button>
        </div>
      ) : (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <span className="text-sm text-amber-800">
              <strong>Sin fecha base configurada.</strong> Los monitores usarán su rango por defecto (últimos 365 días).
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGlobalConfigDialog(true)}
            className="text-amber-600 hover:text-amber-800 hover:bg-amber-100"
          >
            <Settings size={14} className="mr-1" />
            Configurar
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-slate-200 p-1 gap-1">
          <TabsTrigger 
            value="monitors" 
            className="px-4 py-2 rounded-md transition-colors"
            style={{ 
              backgroundColor: activeTab === 'monitors' ? '#dbeafe' : 'transparent',
              color: activeTab === 'monitors' ? '#1e40af' : '#334155'
            }}
          >
            <Activity size={16} className="mr-2" style={{ color: activeTab === 'monitors' ? '#2563eb' : '#64748b' }} />
            <span style={{ color: activeTab === 'monitors' ? '#1e40af' : '#334155' }}>Monitores ({monitors.length})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="groups" 
            className="px-4 py-2 rounded-md transition-colors"
            style={{ 
              backgroundColor: activeTab === 'groups' ? '#dcfce7' : 'transparent',
              color: activeTab === 'groups' ? '#166534' : '#334155'
            }}
          >
            <FolderOpen size={16} className="mr-2" style={{ color: activeTab === 'groups' ? '#22c55e' : '#64748b' }} />
            <span style={{ color: activeTab === 'groups' ? '#166534' : '#334155' }}>Grupos ({groups.length})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="risk-zones" 
            className="px-4 py-2 rounded-md font-medium transition-colors"
            style={{ 
              backgroundColor: activeTab === 'risk-zones' ? '#dc2626' : 'transparent',
              color: activeTab === 'risk-zones' ? '#ffffff' : '#334155'
            }}
          >
            <MapPin size={16} className="mr-2" style={{ color: activeTab === 'risk-zones' ? '#ffffff' : '#64748b' }} />
            <span style={{ color: activeTab === 'risk-zones' ? '#ffffff' : '#334155' }}>Zonas de Riesgo ({riskZones.length})</span>
          </TabsTrigger>
        </TabsList>

        {/* MONITORS TAB */}
        <TabsContent value="monitors" className="space-y-4">
          <div className="flex justify-end">
            <Button
              data-testid="create-monitor-button"
              onClick={openCreateDialog}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={18} className="mr-2" />
              Nuevo Monitor
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {monitors.map((monitor) => (
              <Card
                key={monitor.id}
                data-testid={`monitor-card-${monitor.id}`}
                className="p-6 bg-white border-slate-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${getSeverityColor(monitor.severidad)}`}>
                        {monitor.severidad}
                      </span>
                      {monitor.categoria && (
                        <Badge variant="outline" className="text-xs">
                          {monitor.categoria}
                        </Badge>
                      )}
                      <div className="flex items-center space-x-2">
                        <Switch
                          data-testid={`toggle-monitor-${monitor.id}`}
                          checked={monitor.activo}
                          onCheckedChange={() => handleToggleActive(monitor.id, monitor.activo)}
                        />
                        <span className="text-xs text-slate-600">
                          {monitor.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-slate-900 mb-2">
                      {monitor.nombre}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">{monitor.descripcion}</p>

                    {/* Info del tipo de monitor */}
                    {monitor.tipo_monitor && monitor.tipo_monitor !== 'TRANSACCION_UNICA' && (
                      <div className="text-xs bg-blue-50 text-blue-700 p-2 rounded mb-2">
                        <strong>{MONITOR_TYPES.find(t => t.value === monitor.tipo_monitor)?.label}</strong>
                        {monitor.ventana_temporal && ` · ${monitor.ventana_temporal}`}
                        {monitor.umbral_acumulacion && ` · Umbral: ${monitor.umbral_acumulacion.toLocaleString()}`}
                      </div>
                    )}
                    
                    {/* Info de Lógica Especial y Umbral configurable */}
                    {monitor.logica_especial && (
                      <div className="text-xs p-3 rounded mb-2 border" style={{ backgroundColor: '#f5f3ff', borderColor: '#c4b5fd' }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <strong className="flex items-center gap-1" style={{ color: '#5b21b6' }}>
                              <Settings size={12} style={{ color: '#7c3aed' }} />
                              Lógica: {monitor.logica_especial.replace(/_/g, ' ')}
                            </strong>
                            {monitor.umbral_acumulacion && (
                              <p className="mt-1" style={{ color: '#6b21a8' }}>
                                Umbral actual: <span className="font-bold">{monitor.umbral_acumulacion}</span>
                                {monitor.logica_especial === 'conexiones_compartidas_salida' && ' destinos en común'}
                                {monitor.logica_especial === 'conexiones_compartidas_entrada' && ' orígenes en común'}
                                {monitor.logica_especial === 'dispersion_vinculos' && ' destinos'}
                                {monitor.logica_especial === 'concentrador' && ' orígenes'}
                                {monitor.logica_especial === 'mismo_titular' && ' cuentas'}
                                {monitor.logica_especial === 'uso_automatizado' && ' transacciones/hora'}
                                {monitor.logica_especial === 'crecimiento_volumen' && '% crecimiento'}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs font-medium"
                            style={{ borderColor: '#a78bfa', color: '#6b21a8', backgroundColor: 'white' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const nuevoUmbral = prompt(
                                `Ingrese el nuevo umbral para "${monitor.nombre}":`,
                                monitor.umbral_acumulacion || 2
                              );
                              if (nuevoUmbral !== null && !isNaN(parseFloat(nuevoUmbral))) {
                                handleQuickUpdateUmbral(monitor.id, parseFloat(nuevoUmbral));
                              }
                            }}
                          >
                            <Pencil size={10} className="mr-1" style={{ color: '#7c3aed' }} />
                            <span style={{ color: '#6b21a8' }}>Editar Umbral</span>
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Configuración de umbrales operacionales */}
                    {monitor.logica_especial && ['horarios_inusuales', 'horarios_inusuales_porcentaje', 'uso_automatizado', 'entrada_salida_inmediata'].includes(monitor.logica_especial) && (
                      <div className="text-xs p-3 rounded mb-2 border bg-slate-50 border-slate-200">
                        <p className="font-semibold text-slate-600 mb-2 flex items-center gap-1"><Settings size={11} /> Filtros minimos para generar alerta</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-500">Min. operaciones</label>
                            <input type="number" min="1"
                              value={monitor.parametros?.min_operaciones || ''}
                              placeholder={monitor.logica_especial === 'horarios_inusuales' ? '5' : '10'}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || null;
                                handleUpdateParametros(monitor.id, { ...monitor.parametros, min_operaciones: val });
                              }}
                              className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500">Min. monto ($)</label>
                            <input type="number" min="0"
                              value={monitor.parametros?.min_monto || ''}
                              placeholder="0"
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || null;
                                handleUpdateParametros(monitor.id, { ...monitor.parametros, min_monto: val });
                              }}
                              className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {monitor.criterios && monitor.criterios.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                          Criterios:
                        </p>
                        {monitor.criterios.slice(0, 2).map((criterio, idx) => (
                          <div
                            key={idx}
                            className="text-xs font-mono bg-slate-50 p-2 rounded border border-slate-200"
                          >
                            <span className="text-blue-600">{criterio.campo}</span>
                            <span className="text-slate-500 mx-1">{criterio.operador}</span>
                            <span className="text-green-600">{criterio.valor}</span>
                          </div>
                        ))}
                        {monitor.criterios.length > 2 && (
                          <p className="text-xs text-slate-500">+{monitor.criterios.length - 2} más</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center space-x-2 flex-wrap gap-2">
                    <Button
                      data-testid={`execute-monitor-${monitor.id}`}
                      onClick={() => handleExecuteMonitor(monitor.id, monitor.nombre)}
                      size="sm"
                      disabled={!monitor.activo || !puedeEjecutar}
                      title={puedeEjecutar ? undefined : sinPermisoTxt}
                      className={monitor.activo ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-400 text-white'}
                    >
                      <Play size={14} className="mr-1" />
                      Ejecutar
                    </Button>
                    <Button
                      data-testid={`backtest-monitor-${monitor.id}`}
                      onClick={() => handleBacktestMonitor(monitor)}
                      size="sm"
                      disabled={!puedeEjecutar}
                      title={puedeEjecutar ? undefined : sinPermisoTxt}
                      variant="outline"
                      className="border-purple-200 text-purple-600"
                    >
                      <FlaskConical size={14} className="mr-1" />
                      Backtest
                    </Button>
                    <Button
                      data-testid={`edit-monitor-${monitor.id}`}
                      onClick={() => openEditDialog(monitor)}
                      size="sm"
                      variant="outline"
                    >
                      <Pencil size={14} className="mr-1" />
                      Editar
                    </Button>
                    <Button
                      data-testid={`duplicate-monitor-${monitor.id}`}
                      onClick={() => handleDuplicateMonitor(monitor)}
                      size="sm"
                      variant="outline"
                      className="border-teal-300 text-teal-700"
                      title="Duplicar este monitor"
                    >
                      <Copy size={14} className="mr-1" />
                      Duplicar
                    </Button>
                    <Button
                      data-testid={`delete-monitor-${monitor.id}`}
                      onClick={() => openDeleteDialog(monitor)}
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-600"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {monitors.length === 0 && (
            <div className="text-center py-12">
              <Activity size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-4">No hay monitores configurados</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus size={18} className="mr-2" />
                  Crear Monitor
                </Button>
                <Button onClick={() => setIsInstallDialogOpen(true)} variant="outline" className="border-purple-200 text-purple-700">
                  <Download size={18} className="mr-2" />
                  Instalar Predefinidos
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* GROUPS TAB */}
        <TabsContent value="groups" className="space-y-4">
          <div className="flex justify-end">
            <Button
              data-testid="create-group-button"
              onClick={() => openGroupDialog()}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus size={18} className="mr-2" />
              Nuevo Grupo
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {groups.map((group) => (
              <Card key={group.id} className="p-6 bg-white border-slate-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{group.nombre}</h3>
                    <p className="text-sm text-slate-600 mt-1">{group.descripcion}</p>
                  </div>
                  <Switch
                    checked={group.activo}
                    onCheckedChange={async () => {
                      await api.patch(`/monitor-groups/${group.id}`, { activo: !group.activo });
                      loadGroups();
                    }}
                  />
                </div>

                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div>
                      <p className="text-2xl font-bold text-slate-700">{group.monitores_count || 0}</p>
                      <p className="text-xs text-slate-500">Monitores</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{group.monitores_activos || 0}</p>
                      <p className="text-xs text-slate-500">Activos</p>
                    </div>
                  </div>
                </div>

                {group.monitores && group.monitores.length > 0 && (
                  <div className="space-y-1 mb-4 max-h-32 overflow-y-auto">
                    {group.monitores.map((m) => (
                      <div key={m.id} className="flex items-center justify-between text-xs p-1 bg-slate-50 rounded">
                        <span className="truncate">{m.nombre}</span>
                        <span className={`px-1.5 py-0.5 rounded ${getSeverityColor(m.severidad)}`}>
                          {m.severidad}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200">
                  <Button
                    data-testid={`execute-group-${group.id}`}
                    onClick={() => handleExecuteGroup(group)}
                    size="sm"
                    disabled={!group.activo || group.monitores_activos === 0 || !puedeEjecutar}
                    title={puedeEjecutar ? undefined : sinPermisoTxt}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Play size={14} className="mr-1" />
                    Ejecutar
                  </Button>
                  <Button
                    onClick={() => openGroupDialog(group)}
                    size="sm"
                    variant="outline"
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    onClick={() => handleDeleteGroup(group.id)}
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-600"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {groups.length === 0 && (
            <div className="text-center py-12">
              <FolderOpen size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-4">No hay grupos configurados</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => openGroupDialog()} className="bg-green-600 hover:bg-green-700 text-white">
                  <Plus size={18} className="mr-2" />
                  Crear Grupo
                </Button>
                <Button onClick={() => setIsInstallDialogOpen(true)} variant="outline" className="border-purple-200 text-purple-700">
                  <Download size={18} className="mr-2" />
                  Instalar Predefinidos
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* RISK ZONES TAB */}
        <TabsContent value="risk-zones" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex-1 mr-4">
              <div className="flex items-start gap-2">
                <Shield className="text-amber-600 mt-0.5" size={18} />
                <div className="text-sm text-amber-800">
                  <strong>Zonas de Riesgo:</strong> Configura CBUs, bancos o prefijos que deben ser monitoreados con mayor atención.
                </div>
              </div>
            </div>
            <Button
              data-testid="create-risk-zone-button"
              onClick={() => openRiskZoneDialog()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Plus size={18} className="mr-2" />
              Nueva Zona
            </Button>
          </div>

          <Card className="bg-white border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {riskZones.map((zone) => (
                  <TableRow key={zone.id}>
                    <TableCell className="font-medium">{zone.nombre}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {RISK_ZONE_TYPES.find(t => t.value === zone.tipo)?.label || zone.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{zone.valor}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        zone.nivel_riesgo === 'ALTO' ? 'bg-red-100 text-red-700' :
                        zone.nivel_riesgo === 'MEDIO' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {zone.nivel_riesgo}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs ${zone.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {zone.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openRiskZoneDialog(zone)}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteRiskZone(zone.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {riskZones.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No hay zonas de riesgo configuradas
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ========== DIALOGS ========== */}
      <MonitorsDialogs
        formData={formData} setFormData={setFormData}
        addCriterio={addCriterio} removeCriterio={removeCriterio} updateCriterio={updateCriterio}
        isCreateDialogOpen={isCreateDialogOpen} setIsCreateDialogOpen={setIsCreateDialogOpen} handleCreateMonitor={handleCreateMonitor}
        isEditDialogOpen={isEditDialogOpen} setIsEditDialogOpen={setIsEditDialogOpen} handleUpdateMonitor={handleUpdateMonitor}
        isDeleteDialogOpen={isDeleteDialogOpen} setIsDeleteDialogOpen={setIsDeleteDialogOpen} handleDeleteMonitor={handleDeleteMonitor}
        selectedMonitor={selectedMonitor} saving={saving}
        isBacktestConfigOpen={isBacktestConfigOpen} setIsBacktestConfigOpen={setIsBacktestConfigOpen}
        backtestConfig={backtestConfig} setBacktestConfig={setBacktestConfig} executeBacktest={executeBacktest}
        isBacktestDialogOpen={isBacktestDialogOpen} setIsBacktestDialogOpen={setIsBacktestDialogOpen}
        backtestLoading={backtestLoading} backtestResults={backtestResults}
        selectedBacktestResults={selectedBacktestResults} setSelectedBacktestResults={setSelectedBacktestResults}
        backtestFilters={backtestFilters} setBacktestFilters={setBacktestFilters}
        showBacktestFilters={showBacktestFilters} setShowBacktestFilters={setShowBacktestFilters}
        getFilteredBacktestResults={getFilteredBacktestResults} clearBacktestFilters={clearBacktestFilters}
        toggleBacktestSelection={toggleBacktestSelection} handleCreateHistoricalAlert={handleCreateHistoricalAlert}
        creatingAlert={creatingAlert} creatingCaseFromBacktest={creatingCaseFromBacktest} handleCreateCaseFromBacktest={handleCreateCaseFromBacktest}
        isGroupDialogOpen={isGroupDialogOpen} setIsGroupDialogOpen={setIsGroupDialogOpen}
        selectedGroup={selectedGroup} groupFormData={groupFormData} setGroupFormData={setGroupFormData}
        monitors={monitors} toggleMonitorInGroup={toggleMonitorInGroup} handleSaveGroup={handleSaveGroup}
        isGroupExecutionOpen={isGroupExecutionOpen} setIsGroupExecutionOpen={setIsGroupExecutionOpen}
        groupExecutionLoading={groupExecutionLoading} groupExecutionResults={groupExecutionResults}
        isInstallDialogOpen={isInstallDialogOpen} setIsInstallDialogOpen={setIsInstallDialogOpen}
        handleInstallPredefined={handleInstallPredefined} installing={installing}
        isRiskZoneDialogOpen={isRiskZoneDialogOpen} setIsRiskZoneDialogOpen={setIsRiskZoneDialogOpen}
        riskZoneFormData={riskZoneFormData} setRiskZoneFormData={setRiskZoneFormData} handleSaveRiskZone={handleSaveRiskZone}
        isExecuteAllDialogOpen={isExecuteAllDialogOpen} setIsExecuteAllDialogOpen={setIsExecuteAllDialogOpen}
        executeAllLoading={executeAllLoading} executeAllResults={executeAllResults} setExecuteAllResults={setExecuteAllResults}
        generateAlertsAfterBacktest={generateAlertsAfterBacktest} handleGenerateAlertsFromResults={handleGenerateAlertsFromResults}
        alertGenerationMode={alertGenerationMode} setAlertGenerationMode={setAlertGenerationMode}
        selectedMonitorsForAlerts={selectedMonitorsForAlerts} setSelectedMonitorsForAlerts={setSelectedMonitorsForAlerts}
        forzarNuevosCasos={forzarNuevosCasos} setForzarNuevosCasos={setForzarNuevosCasos}
        modoEjecucion={modoEjecucion} setModoEjecucion={setModoEjecucion}
        clienteSearch={clienteSearch} setClienteSearch={setClienteSearch}
        clienteFilter={clienteFilter} setClienteFilter={setClienteFilter}
        clientesEncontrados={clientesEncontrados} setClientesEncontrados={setClientesEncontrados}
        buscandoCliente={buscandoCliente}
        clienteSeleccionado={clienteSeleccionado} setClienteSeleccionado={setClienteSeleccionado}
        showGlobalConfigDialog={showGlobalConfigDialog} setShowGlobalConfigDialog={setShowGlobalConfigDialog}
        globalConfig={globalConfig} setGlobalConfig={setGlobalConfig} saveGlobalConfig={saveGlobalConfig}
      />
    </div>
  );
};

export default Monitors;

